import { AfterContentInit, AfterViewInit, Component, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { AdminService } from '../../services/admin.service';
import { ServerResponseObject } from '../../interfaces/ServerResponse';
import { User, Users } from '../../interfaces/admin/Users';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { AddUserDialogComponent } from '../../components/dialogs/add-user-dialog/add-user-dialog.component';
import { MatMenuModule } from '@angular/material/menu';
import { AddPermissionsUserDialogComponent } from '../../components/dialogs/add-permissions-user-dialog/add-permissions-user-dialog.component';
import { Companies, Company } from '../../interfaces/admin/Company';
import { AddCompanyDialogComponent } from '../../components/dialogs/add-company-dialog/add-company-dialog.component';
import { GetDataService, GetDataServices } from '../../interfaces/DataService';
import { AddDataserviceDialogComponent } from '../../components/dialogs/add-dataservice-dialog/add-dataservice-dialog.component';
import { UserDialogData, AddUserCompanyDialogComponent } from '../../components/dialogs/add-user-company-dialog/add-user-company-dialog.component';
import { AddUserDataserviceDialogComponent } from '../../components/dialogs/add-user-dataservice-dialog/add-user-dataservice-dialog.component';
import { Pagination } from '../../clases/Pagination';
import { PaginationComponent } from '../../components/admin/pagination/pagination.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { TreeModelsDialogComponent } from '../../components/dialogs/tree-models-dialog/tree-models-dialog.component';
import { SharedService } from '../../services/shared.service';
import { NotifyDialogService } from '../../services/notify-dialog.service';
import { AddSchemaPermissionsUserDialogComponent } from '../../components/dialogs/add-schema-permissions-user-dialog/add-schema-permissions-user-dialog.component';

type Role = 'admin' | 'editor' | 'viewer';

interface DataServiceItem {
  company_id: string;
  dataservice_id: string;
  dataservice_name: string;
  contact: string;
  phone: string;
  description: string;
  host: string;
  port: string;
  database_name: string;
  user: string;
  password: string;
}

@Component({
  selector: 'app-admin-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PaginationComponent,
    // Material
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    MatCardModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    MatExpansionModule
  ],
  templateUrl: './admin-panel.component.html',
  styleUrl: './admin-panel.component.scss'
})
export class AdminPanelComponent implements AfterViewInit{
  selectedSection: 'users' | 'company' | 'dataservices' | 'settings' = 'users';
  panelTitle: string = "Users";

  displayedColumns: string[] = ['name', 'email', 'country', 'actions'];
  companyDisplayedColumns: string[] = [
    'company_id',
    'company_name',
    'country_head_quarters',
    'description',
  ];
  dataServiceDisplayedColumns: string[] = [
    'company_id',
    'dataservice_id',
    'dataservice_name',
    'type',
    'contact',
    'phone',
    'description',
    'actions'
  ];

  roles: Role[] = ['admin', 'editor', 'viewer'];

  isLoading: boolean = false;

  usersPagination: Pagination = new Pagination;
  companiesPagination: Pagination = new Pagination;
  dataservicePagination: Pagination = new Pagination;

  @ViewChild('userPaginationComponent', { static: false }) userPaginationComponent!: PaginationComponent;
  @ViewChild('companiesPaginationComponent', { static: false }) companiesPaginationComponent!: PaginationComponent;
  @ViewChild('dataservicesPaginationComponent', { static: false }) dataservicesPaginationComponent!: PaginationComponent;

  dataSource = new MatTableDataSource<User>();
  companyDataSource = new MatTableDataSource<Company>();
  dataServiceDataSource = new MatTableDataSource<GetDataService>();

  filterValue = '';
  companyFilterValue = '';
  dataServiceFilterValue = '';

  readonly dialog = inject(MatDialog);

  currentUserSelected: User | undefined;
  currentDataserviceSelected: GetDataService | undefined;

  constructor(private adminService: AdminService, private sharedService: SharedService, private notify: NotifyDialogService) { }

  ngAfterViewInit(): void {
    this.callUserList();
  }

  onChangeSecction(){
    switch(this.selectedSection){
      case "users":
        this.panelTitle = "Users";
      break;
      case "company":
        this.panelTitle = "Companies";
      break;
      case "dataservices":
        this.panelTitle = "Data Services";
      break;
      case "settings":
        this.panelTitle = "Settings";
      break;
    }
  }

  callUserList(){
    setTimeout(()=>{
      this.userPaginationComponent.updateDataSource();  
    }, 0);
  }

  callCompaniesList() {
    setTimeout(() => {
      this.companiesPaginationComponent.updateDataSource();
    }, 0);
  }

  callDataservicesList() {
    setTimeout(() => {
      this.dataservicesPaginationComponent.updateDataSource();
    }, 0);
  }

  safeGetUsers(){
    if(!this.dataSource.data.length) this.callUserList();
  }

  safeGetCompanies(){
    if(!this.companyDataSource.data.length) this.callCompaniesList();
  }

  safeGetDataservices(){
    if(!this.dataServiceDataSource.data.length) this.callDataservicesList();
  }

  onAddUser(){
    const ref = this.dialog.open(AddUserDialogComponent, {
      data: {},
      panelClass: '',
    });
    ref.afterClosed().subscribe({
      next: (wasSucessfull: boolean)=>{
        if(wasSucessfull) this.callUserList();
      }
    });
  }

  onModifyUser(){
    if (!this.currentUserSelected) return;
    const ref = this.dialog.open(AddUserDialogComponent, {
      data: {
        user: this.currentUserSelected
      },
      panelClass: '',
    });
    ref.afterClosed().subscribe({
      next: (wasSucessfull: boolean)=>{
        if(wasSucessfull) this.callUserList();
      }
    });
  }

  selectUser(user: User){
    this.currentUserSelected = user;
  }

  onAddRole(){
    if (!this.currentUserSelected) return;
    const ref = this.dialog.open(AddPermissionsUserDialogComponent, {
      data: {
        user_id: this.currentUserSelected.id,
        name: this.currentUserSelected.name,
        email: this.currentUserSelected.email,
        uuid: this.currentUserSelected.uuid
      } as UserDialogData,
      // width: "600px",
      panelClass: 'dialog-smallx2-landscape',
    });
  }

  onAddSchemaPermissions(){
    if (!this.currentUserSelected) return;
    this.dialog.open(AddSchemaPermissionsUserDialogComponent, {
      data: {
        user_id: this.currentUserSelected.id,
        name: this.currentUserSelected.name,
        email: this.currentUserSelected.email,
        uuid: this.currentUserSelected.uuid
      } as UserDialogData,
      panelClass: 'dialog-smallx2-landscape',
    });
  }

  onAddUserCompany() {
    if (!this.currentUserSelected) return;
    const ref = this.dialog.open(AddUserCompanyDialogComponent, {
      data: {
        user_id: this.currentUserSelected.id,
        name: this.currentUserSelected.name,
        email: this.currentUserSelected.email,
        uuid: this.currentUserSelected.uuid
      } as UserDialogData,
      panelClass: 'dialog-smallx1-landscape',
    });
    ref.afterClosed().subscribe({
      next: (wasSucessfull: boolean) => {
        if (wasSucessfull) this.callUserList();
      }
    });
  }

  onAddUserDataService() {
    if (!this.currentUserSelected) return;
    const ref = this.dialog.open(AddUserDataserviceDialogComponent, {
      data: {
        user_id: this.currentUserSelected.email,
        name: this.currentUserSelected.name,
        email: this.currentUserSelected.email
      } as UserDialogData,
      panelClass: 'dialog-smallx1-landscape',
    });
    ref.afterClosed().subscribe({
      next: (wasSucessfull: boolean) => {
        if (wasSucessfull) this.callUserList();
      }
    });
  }

  getUsersList = (callback: Function)=>{
    this.isLoading = true;
    this.adminService.ListUsers(this.filterValue, this.usersPagination.currentPage, this.usersPagination.pageSize).subscribe({
      next: (res: ServerResponseObject<Users>)=>{
        this.dataSource.data = res.result.users;
        callback(res.result.regs || 0);
        this.isLoading = false;
      },
      error: ()=>{
        this.isLoading = false;
      }
    });
  }

  onAddCompany(){
    const ref = this.dialog.open(AddCompanyDialogComponent, {
      data: {},
      panelClass: '',
    });
    ref.afterClosed().subscribe({
      next: (wasSucessfull: boolean)=>{
        if(wasSucessfull) this.callCompaniesList();
      }
    });
  }

  getCompaniesList = (callback: Function)=> {
    this.adminService.ListCompanies(this.companyFilterValue, this.companiesPagination.currentPage, this.companiesPagination.pageSize).subscribe({
      next: (res: ServerResponseObject<Companies>) => {
        this.companyDataSource.data = res.result.companies;
        callback(res.result.regs || 0);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  onAddDataService(){
    const ref = this.dialog.open(AddDataserviceDialogComponent, {
      data: {},
      panelClass: '',
    });
    ref.afterClosed().subscribe({
      next: (wasSucessfull: boolean)=>{
        if(wasSucessfull) this.callDataservicesList();
      }
    });
  }

  getDataServiceList = (callback: Function)=> {
    this.adminService.ListDataServices(this.dataServiceFilterValue, this.dataservicePagination.currentPage, this.dataservicePagination.pageSize).subscribe({
      next: (res: ServerResponseObject<GetDataServices>) => {
        this.dataServiceDataSource.data = res.result.dataservices;
        callback(res.result.regs || 0);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  configModelTree(){
    let dataservice = this.sharedService.data_services?.find(ds => ds.data_service_id == this.currentDataserviceSelected!.id);
    if(!dataservice){
      this.notify.openDialog("Permissions error","You don't have permissions to modify that dataservice.")
      return;
    }
    const ref = this.dialog.open(TreeModelsDialogComponent, {
      data: {dataservice},
      panelClass: 'full-dialog',
    });
    ref.afterClosed().subscribe({
      next: (wasSucessfull: boolean)=>{
      }
    });
  }

  selectDataservice(selected: GetDataService){
    this.currentDataserviceSelected = selected;
  }

  
  clearFilter() {
    this.filterValue = '';
    this.usersPagination.currentPage = 1;
    this.usersPagination.pageInput = 1;
    this.callUserList();
  }

  clearCompanyFilter() {
    this.companyFilterValue = '';
    this.companiesPagination.currentPage = 1;
    this.companiesPagination.pageInput = 1;
    this.callCompaniesList();
  }

  clearDataServiceFilter() {
    this.dataServiceFilterValue = '';
    this.dataservicePagination.currentPage = 1;
    this.dataservicePagination.pageInput = 1;
    this.callDataservicesList();
  }

  trackById = (_: number, item: User) => item.id;

  trackByCompanyId = (_: number, item: Company) => item.id;

  trackByDataServiceId = (_: number, item: GetDataService) => item.id;
}
